// LOCATION: admin-portal/src/pages/JourneyLogs.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase'; 
import { collection, collectionGroup, getDocs, query, where, doc, getDoc, writeBatch } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './UserManagement.css'; 

const JourneyLogs = () => {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('30d');
  const [paymentFilter, setPaymentFilter] = useState('All');
  const [allSessions, setAllSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [bizTypeFilter, setBizTypeFilter] = useState('All');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startTimeMs = 0;
      if (filter === '24h') startTimeMs = now.getTime() - (24 * 60 * 60 * 1000);
      if (filter === '7d') startTimeMs = now.getTime() - (7 * 24 * 60 * 60 * 1000);
      if (filter === '30d') startTimeMs = now.getTime() - (30 * 24 * 60 * 60 * 1000);
      
      const q = startTimeMs > 0 
        ? query(collectionGroup(db, 'sessions'), where('updatedAtMs', '>=', startTimeMs))
        : query(collectionGroup(db, 'sessions'));

      const sessionSnap = await getDocs(q);
      
      if (sessionSnap.empty) {
        setAllSessions([]);
        setLoading(false);
        return;
      }

      const profileCache = {};

      const processed = await Promise.all(sessionSnap.docs.map(async (sDoc) => {
        const data = sDoc.data();
        const fullPath = sDoc.ref.path;
        const pathParts = fullPath.split('/');
        let userId = pathParts[pathParts.indexOf('journeys') + 1];
        if (!userId) return null;

        if (!profileCache[userId]) {
            try {
                const profileSnap = await getDoc(doc(db, "profiles", userId));
                profileCache[userId] = profileSnap.exists() ? profileSnap.data() : { name: "User Not Found", email: "N/A" };
            } catch (e) { profileCache[userId] = { name: "Error Fetching", email: "N/A" }; }
        }

        const pData = profileCache[userId];

        // ✅ NEW LOGIC: Pull Snapshot Count OR Fallback to Manual Contacts
        let finalContactCount = 0;
        
        // 1. Check if the session document itself has a locked count
        if (data.emergencyContactCount !== undefined) {
            finalContactCount = data.emergencyContactCount;
        } else {
            // 2. Fallback for older sessions: Fetch and filter for EXTERNAL_EMERGENCY only
            try {
              const cSnap = await getDocs(collection(db, "users", userId, "contacts"));
              finalContactCount = cSnap.docs.filter(d => d.data().type === "EXTERNAL_EMERGENCY").length;
            } catch (e) { console.warn("Fallback contact count failed", userId); }
        }

        const bizType = pData.businessType || 'User';
        const bizCat = pData.businessCategory || '';
        
        // ✅ NEW BILLING: ₦500 per contact
        let paymentAmount = "N/A";
        if (bizType === "Business" && bizCat === "Transport") {
            paymentAmount = `₦${(finalContactCount * 500).toLocaleString()}`;
        }

        return {
          id: sDoc.id,
          uid: userId,
          fullPath: fullPath,
          userName: pData.name,
          userEmail: pData.email,
          destName: data.destName || "N/A",
          bizType,
          bizCat,
          contactCount: finalContactCount,
          paymentAmount,
          paymentStatus: data.paymentStatus || "Pending",
          status: data.status || 'PENDING',
          updatedAtMs: data.updatedAtMs || 0,
          updatedAt: data.updatedAtMs ? new Date(data.updatedAtMs).toLocaleString() : 'N/A',
        };
      }));

      setAllSessions(processed.filter(p => p !== null).sort((a, b) => b.updatedAtMs - a.updatedAtMs));
      
    } catch (e) { 
      console.error("Fetch Error:", e); 
    }
    setLoading(false);
  };

  // ... [Keep filteredHistory, exportToExcel, markSelectedAsPaid logic unchanged] ...

  // ✅ Ensure Memoized filter logic remains consistent
  const filteredHistory = useMemo(() => {
    return allSessions.filter(j => {
        const matchesSearch = j.userName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             j.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             j.destName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = bizTypeFilter === 'All' || j.bizType === bizTypeFilter;
        const matchesPayment = paymentFilter === 'All' || j.paymentStatus === paymentFilter;

        return matchesSearch && matchesType && matchesPayment;
    });
  }, [allSessions, searchTerm, bizTypeFilter, paymentFilter]);

  const exportToExcel = () => {
    if (filteredHistory.length === 0) return alert("No data to export");

    const dataToExport = filteredHistory.map(j => ({
      "User Name": j.userName,
      "User Email": j.userEmail,
      "Destination": j.destName,
      "Account Type": j.bizType,
      "Category": j.bizCat || "N/A",
      "Emergency Contacts": j.contactCount,
      "Journey Status": j.status,
      "Billing Amount": j.paymentAmount,
      "Payment Status": j.paymentStatus,
      "Date": j.updatedAt
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Journey Logs");
    const fileName = `Ccuring_Logs_${paymentFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const markSelectedAsPaid = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Mark ${selectedIds.length} selected sessions as PAID?`)) return;

    const batch = writeBatch(db);
    selectedIds.forEach(id => {
        const session = allSessions.find(s => s.id === id);
        if (session) {
            batch.update(doc(db, session.fullPath), { 
                paymentStatus: "Paid",
                paidAt: new Date()
            });
        }
    });

    try {
        await batch.commit();
        alert("Success: Selected sessions marked as Paid.");
        fetchLogs();
        setSelectedIds([]);
    } catch (error) {
        console.error("Batch update failed:", error);
        alert("Failed to update records.");
    }
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
        setSelectedIds(filteredHistory.map(j => j.id));
    } else {
        setSelectedIds([]);
    }
  };

  if (loading) return <div className="container" style={{padding: '40px', textAlign: 'center'}}><h2>🔍 Syncing Session Records...</h2></div>;

  return (
    <div className="container" style={{padding: '24px'}}>
      <div className="header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1>📂 Detailed Session Logs</h1>
        <div style={{display:'flex', gap:'12px', flexWrap: 'wrap'}}>
            <button onClick={exportToExcel} className="export-btn" style={{background: '#4f46e5', color: 'white'}}>
                📊 Export to Excel
            </button>
            <input type="text" placeholder="Search..." className="search-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <select className="type-select" value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
                <option value="All">All Payment Status</option>
                <option value="Paid">Paid Only</option>
                <option value="Pending">Pending Only</option>
            </select>
            <select className="type-select" value={bizTypeFilter} onChange={e => setBizTypeFilter(e.target.value)}>
                <option value="All">All Accounts</option>
                <option value="User">Standard Users</option>
                <option value="Business">Business Accounts</option>
            </select>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="type-select">
                <option value="24h">24h</option>
                <option value="7d">7 Days</option>
                <option value="30d">30 Days</option>
                <option value="all">All Time</option>
            </select>
        </div>
      </div>

      <div className="table-section" style={{marginTop: '20px'}}>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'15px', alignItems:'center'}}>
            <span style={{color: '#6b7280', fontSize:'14px'}}>Viewing {filteredHistory.length} sessions</span>
            {selectedIds.length > 0 && (
                <button onClick={markSelectedAsPaid} className="action-btn" style={{background: '#10b981', color: 'white', fontWeight:'bold'}}>
                    💰 Mark {selectedIds.length} as Paid
                </button>
            )}
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th style={{width:'40px'}}>
                  <input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length === filteredHistory.length && filteredHistory.length > 0} />
              </th>
              <th>Details</th>
              <th>Destination</th>
              <th>Account</th>
              <th>Contacts</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Payment</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredHistory.map(j => (
              <tr key={j.id}>
                <td>
                  <input type="checkbox" checked={selectedIds.includes(j.id)} onChange={() => setSelectedIds(prev => prev.includes(j.id) ? prev.filter(id => id !== j.id) : [...prev, j.id])} />
                </td>
                <td>
                  <div style={{fontWeight: 'bold'}}>{j.userName}</div>
                  <div style={{fontSize: '11px', color: '#6366f1'}}>{j.userEmail}</div>
                </td>
                <td><small>{j.destName}</small></td>
                <td>
                  <span className={j.bizType === "Business" ? "badge badge-purple" : "badge badge-green"}>{j.bizType}</span>
                </td>
                <td style={{textAlign: 'center'}}>{j.contactCount}</td>
                <td style={{fontWeight: 'bold', fontSize: '11px'}}>{j.status}</td>
                <td>{j.paymentAmount}</td>
                <td>
                  <span className={`badge ${j.paymentStatus === "Paid" ? "badge-green" : "badge-orange"}`}>
                      {j.paymentStatus}
                  </span>
                </td>
                <td><small>{j.updatedAt}</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JourneyLogs;